/**
 * PostHog for merchant usage and session replay (BACKEND_PLAN.md §7b). Loaded after first paint so it never
 * delays the admin's LCP. Inputs are masked; no names or emails are sent, only the Shopify user ID and shop.
 */

type Identity = { shopId: string; shopDomain: string; userId: string; plan: string };

const KEY = import.meta.env.VITE_POSTHOG_KEY;
const HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

/** Shopify's launch parameters: the ID token, HMAC and session must never reach analytics. */
const SENSITIVE_PARAMS = ['id_token', 'hmac', 'session', 'host', 'timestamp', 'shop'];
const URL_PROPERTIES = ['$current_url', '$initial_current_url', '$referrer', '$initial_referrer'];

export function sanitizeUrl(value: unknown): unknown {
  if (typeof value !== 'string' || !value.includes('?')) return value;
  try {
    const url = new URL(value);
    SENSITIVE_PARAMS.forEach(param => url.searchParams.delete(param));
    return url.toString();
  } catch {
    return value;
  }
}

type Props = Record<string, unknown>;
type CapturedEvent = { properties?: Props; $set?: Props; $set_once?: Props } | null;

function sanitizeProps(props: Props | undefined): void {
  if (!props) return;
  for (const key of URL_PROPERTIES) if (key in props) props[key] = sanitizeUrl(props[key]);
  // Session replay "meta" snapshots (type 4) carry the page href.
  const snapshots = props.$snapshot_data;
  if (Array.isArray(snapshots)) {
    for (const snap of snapshots as { type?: number; data?: { href?: unknown } }[]) {
      if (snap?.type === 4 && snap.data) snap.data.href = sanitizeUrl(snap.data.href);
    }
  }
}

/** PostHog `before_send`: strips launch parameters from every URL property, including replay snapshots. */
export function scrubEvent<T extends CapturedEvent>(event: T): T {
  if (!event) return event;
  sanitizeProps(event.properties);
  sanitizeProps(event.$set);
  sanitizeProps(event.$set_once);
  sanitizeProps(event.properties?.$set as Props | undefined);
  sanitizeProps(event.properties?.$set_once as Props | undefined);
  return event;
}

/** Reads `sub` (the Shopify staff user ID) from an ID token. Not a security check: the API verifies tokens. */
export function userIdFromToken(token: string): string {
  try {
    const payload = token.split('.')[1] ?? '';
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { sub?: unknown };
    return typeof json.sub === 'string' ? json.sub : '';
  } catch {
    return '';
  }
}

export const whenIdle = (fn: () => void) =>
  'requestIdleCallback' in window ? window.requestIdleCallback(fn, { timeout: 3000 }) : setTimeout(fn, 1500);

export function startAnalytics(identity: Identity): void {
  if (!KEY) return;
  whenIdle(async () => {
    const { default: posthog } = await import('posthog-js');
    posthog.init(KEY, {
      api_host: HOST,
      person_profiles: 'identified_only',
      capture_pageview: 'history_change',
      session_recording: { maskAllInputs: true },
      before_send: event => scrubEvent(event),
    });
    posthog.register({ app_version: __APP_VERSION__ });
    if (identity.userId) posthog.identify(`${identity.shopId}:${identity.userId}`);
    posthog.group('shop', identity.shopId, { domain: identity.shopDomain, plan: identity.plan });
  });
}
